import React, { useCallback, useMemo } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Card, FormControl, IconButton, InputLabel, MenuItem, Select, Stack } from '@mui/material';

import type { CommissionOption, CommissionsConditionTypes, RuleCondition } from '~/consts/agentHub';

import { AddNewConditionForm } from './AddNewConditionForm';
import {
  CONDITION_OPERATORS,
  CONDITION_OPERATOR_SYMBOL,
  isEqOnlyCommissionConditionType,
} from './CommissionDialog.types';
import type {
  AddConditionHandler,
  ChangeConditionHandler,
  ChangeConditionOperatorHandler,
  CommissionConditionForm,
  CommissionConditionOperator,
  CommissionGroupOperator,
  ConditionIdHandler,
} from './CommissionDialog.types';

interface Props {
  groupId: CommissionGroupOperator;
  condition: CommissionConditionForm;
  brand: string;
  addCondition: AddConditionHandler;
  handleChangeCondition: ChangeConditionHandler;
  handleConditionOperatorChange: ChangeConditionOperatorHandler;
  handleClearCondition: ConditionIdHandler;
}

export const CommissionConditionEditor = React.memo(function CommissionConditionEditor({
  groupId,
  condition,
  brand,
  addCondition,
  handleChangeCondition,
  handleConditionOperatorChange,
  handleClearCondition,
}: Props) {
  const handleAddCondition = useCallback(
    (newCondition: RuleCondition) => addCondition(groupId, condition.id, newCondition),
    [addCondition, condition.id, groupId],
  );

  const handleFlightCarrierChange = useCallback(
    (next: CommissionOption) => addCondition(groupId, condition.id, { type: 'flightCarrier', value: next }),
    [addCondition, condition.id, groupId],
  );

  const handleChangeConditionType = useCallback(
    (_: CommissionsConditionTypes, type: CommissionsConditionTypes) =>
      handleChangeCondition(groupId, condition.id, type),
    [condition.id, groupId, handleChangeCondition],
  );

  const handleOperatorChange = useCallback(
    (event: { target: { value: unknown } }) =>
      handleConditionOperatorChange(groupId, condition.id, event.target.value as CommissionConditionOperator),
    [condition.id, groupId, handleConditionOperatorChange],
  );

  const handleRemoveCondition = useCallback(
    () => handleClearCondition(groupId, condition.id),
    [condition.id, groupId, handleClearCondition],
  );

  const conditionOperators = useMemo(() => {
    if (isEqOnlyCommissionConditionType(condition.type)) {
      return CONDITION_OPERATORS.filter((operator) => operator === 'eq');
    }
    return CONDITION_OPERATORS;
  }, [condition.type]);

  const conditionOperatorValue = conditionOperators.includes(condition.operator) ? condition.operator : 'eq';

  return (
    <Card variant="outlined" sx={{ borderRadius: 1, p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <FormControl size="small" sx={{ minWidth: 108 }}>
          <InputLabel
            sx={{ backgroundColor: 'background.paper', padding: '0 4px' }}
            shrink
            id={`condition-operator-${condition.id}`}
          >
            Operator
          </InputLabel>
          <Select
            labelId={`condition-operator-${condition.id}`}
            value={conditionOperatorValue}
            label="Operator"
            sx={{ borderRadius: 0.5 }}
            onChange={handleOperatorChange}
          >
            {conditionOperators.map((operator) => (
              <MenuItem key={operator} value={operator}>
                {CONDITION_OPERATOR_SYMBOL[operator]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box flex={1}>
          <AddNewConditionForm
            key={`${condition.id}-${condition.type}`}
            brand={brand}
            conditionType={condition.type}
            ruleConditions={condition.value}
            addCondition={handleAddCondition}
            handleChangeCondition={handleChangeConditionType}
            onFlightCarrierChange={handleFlightCarrierChange}
          />
        </Box>
        <IconButton aria-label={`Remove ${condition.type} condition`} size="small" onClick={handleRemoveCondition}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Card>
  );
});
